import { useState } from "react";
import { CheckCircle2, KeyRound, Loader2, Mail } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Field = ({ label, ...props }) => (
  <div>
    <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">
      {label}
    </label>
    <Input {...props}
      className="h-11 border-ink-600 bg-ink-900 text-slate-100 focus-visible:ring-gold" />
  </div>
);

/** Change your own sign-in email and password, for any signed-in account. */
export const AccountSecurity = () => {
  const { user, login } = useAuth();
  const [mode, setMode] = useState("password");
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => setForm({ current_password: "", new_password: "", confirm: "", email: "" });

  const savePassword = async () => {
    if (form.new_password !== form.confirm) {
      setError("Those two passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/account/password", {
        current_password: form.current_password, new_password: form.new_password });
      // Changing the password ends every existing session, including this one, so mint a fresh
      // token straight away rather than bouncing the user to the login screen.
      await login({ email: user.email, password: form.new_password });
      reset();
      setDone("Password changed. Any other device signed in as you has been signed out.");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const saveEmail = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/account/email", {
        current_password: form.current_password, email: form.email });
      reset();
      setDone(`Sign-in email is now ${data.email}. Use it next time you sign in.`);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="account-security" className="rounded-xl border border-ink-600 bg-ink-800 p-6">
      <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Your sign-in</p>
      <p className="mt-1.5 text-sm text-slate-400">
        Signed in as <span className="text-slate-200">{user?.email}</span>
      </p>

      <div className="mt-5 flex gap-2">
        {[["password", "Change password", KeyRound], ["email", "Change email", Mail]].map(
          ([k, label, Icon]) => (
            <button key={k} type="button" data-testid={`security-tab-${k}`}
              onClick={() => { setMode(k); reset(); setError(""); setDone(""); }}
              className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs transition-colors ${
                mode === k ? "border-gold/50 bg-gold/10 text-gold"
                  : "border-ink-600 text-slate-400 hover:text-slate-200"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
      </div>

      {done && (
        <p data-testid="security-done" className="mt-5 flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {done}
        </p>
      )}

      <div className="mt-5 max-w-md space-y-4">
        <Field label="Current password" type="password" autoComplete="current-password"
          data-testid="security-current" value={form.current_password} onChange={set("current_password")} />

        {mode === "password" ? (
          <>
            <Field label="New password (10 characters or more)" type="password"
              autoComplete="new-password" data-testid="security-new"
              value={form.new_password} onChange={set("new_password")} />
            <Field label="New password again" type="password" autoComplete="new-password"
              data-testid="security-confirm" value={form.confirm} onChange={set("confirm")} />
          </>
        ) : (
          <Field label="New sign-in email" type="email" data-testid="security-email"
            value={form.email} onChange={set("email")} />
        )}

        {error && <p data-testid="security-error" className="text-sm text-red-400">{error}</p>}

        <Button data-testid="security-submit"
          disabled={busy || !form.current_password ||
            (mode === "password" ? form.new_password.length < 10 : !form.email.includes("@"))}
          onClick={mode === "password" ? savePassword : saveEmail}
          className="bg-gold text-ink-900 hover:bg-gold-light">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" />
            : mode === "password" ? "Change my password" : "Change my email"}
        </Button>

        <p className="text-xs text-slate-500">
          {mode === "password"
            ? "Changing your password signs you out everywhere else. You will stay signed in here."
            : "This is the address you sign in with — and where your Monday summary and feedback alerts are sent."}
        </p>
      </div>
    </div>
  );
};
